import { action$, ofType } from '@ardoq/rxbeach';
import { combineLatest, merge } from 'rxjs';
import { map } from 'rxjs/operators';
import type { PerspectivesFormattingOptions } from '@ardoq/perspectives-sidebar';
import { filterInterface } from '@ardoq/filter-interface';
import { fieldInterface } from '@ardoq/field-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { editorOpened, notifyPerspectiveApplied } from 'perspective/actions';
import { fields$, FieldsState } from 'streams/fields/fields$';
import { context$ } from '../context/context$';
import { ArdoqId } from '@ardoq/api-types';
import { ComponentLabelSource, ReferenceLabelSource } from '@ardoq/data-model';
import { isTruthy } from '@ardoq/common-helpers';
import { perspectiveInterface } from 'modelInterface/perspectives/perspectiveInterface';
import { mapToPerspectiveEditorFormattingRule } from './mapToPerspectiveEditorFormattingRule';
import {
  notifyFilterColorChanged,
  notifyFiltersChanged,
} from '../filters/FilterActions';
import { uniq, uniqBy } from 'lodash';
import {
  getAppliedComponentFormattingLabelData,
  getAppliedReferenceFormattingLabelData,
  getReferenceFormattingCustomLabels,
  getComponentFormattingCustomLabels,
  getFormattingCustomLabelsByFields,
  toCustomLabelFormattingOption,
} from './getFormattingOptionsUtils';
import {
  ViewpointOptionsSourceData,
  viewpointOptionsSourceData$,
} from './viewpointOptionsSourceData$';
import { getIsShowReferenceTypeApplied } from '@ardoq/graph';
import {
  getConnectedWorkspaceIds,
  linkedWorkspaces$,
} from 'streams/linkedWorkspaces$';
import { hasDefaultGreyColorFilter } from 'models/utils/filterUtils';

export const getAppliedConditionalFormattingRules = () =>
  filterInterface
    .getFormattingFilters()
    .map(mapToPerspectiveEditorFormattingRule)
    .filter(isTruthy);

const getFieldNameFromLabelFormatting = (
  labelFormattingValue: string,
  defaultLabelFormattingValues: string[]
) => {
  if (
    !labelFormattingValue ||
    defaultLabelFormattingValues.includes(labelFormattingValue)
  ) {
    return null;
  }
  return fieldInterface.getGlobalFieldByName(labelFormattingValue);
};

const getFormattingOptionsIncludingSavedPerspectiveEntriesFromClosedWorkspaces =
  (
    workspaceIds: ArdoqId[],
    fields: FieldsState
  ): PerspectivesFormattingOptions => {
    const {
      referenceFormattingCustomLabels:
        referenceFormattingCustomLabelsOfOpenedWorkspaces,
      componentFormattingCustomLabels:
        componentFormattingCustomLabelsOfOpenedWorkspaces,
      appliedComponentFormattingLabel,
      appliedReferenceFormattingLabel,
      isAppliedComponentLabelTimeIncluded,
      isAppliedReferenceLabelTimeIncluded,
      appliedConditionalFormattingRules,
    } = getFormattingOptionsOfOpenedWorkspaces(workspaceIds, fields);

    const componentFieldNameFromAppliedLabelFormatting =
      getFieldNameFromLabelFormatting(
        appliedComponentFormattingLabel,
        Object.values(ComponentLabelSource)
      );
    const componentLabelFormattingOptionsWithCustomFieldName = [
      componentFieldNameFromAppliedLabelFormatting,
    ]
      .filter(isTruthy)
      .map(toCustomLabelFormattingOption);

    const referenceFieldNameFromAppliedLabelFormatting =
      getFieldNameFromLabelFormatting(
        appliedReferenceFormattingLabel,
        Object.values(ReferenceLabelSource)
      );
    const referenceLabelFormattingOptionsWithCustomFieldName = [
      referenceFieldNameFromAppliedLabelFormatting,
    ]
      .filter(isTruthy)
      .map(toCustomLabelFormattingOption);

    const componentFormattingCustomLabels = uniqBy(
      [
        ...componentFormattingCustomLabelsOfOpenedWorkspaces,
        ...componentLabelFormattingOptionsWithCustomFieldName,
      ],
      'value'
    );
    const referenceFormattingCustomLabels = uniqBy(
      [
        ...referenceFormattingCustomLabelsOfOpenedWorkspaces,
        ...referenceLabelFormattingOptionsWithCustomFieldName,
      ],
      'value'
    );

    return {
      appliedConditionalFormattingRules,
      appliedComponentFormattingLabel,
      appliedReferenceFormattingLabel,
      isAppliedComponentLabelTimeIncluded,
      isAppliedReferenceLabelTimeIncluded,
      referenceFormattingCustomLabels,
      componentFormattingCustomLabels,
      appliedLabelFormatting: filterInterface.getLabelFormatting(),
      appliedShowReferenceType: getIsShowReferenceTypeApplied(
        filterInterface.getAllReferenceLabelFilters()
      ),
      appliedKeepOriginalColor: !hasDefaultGreyColorFilter(
        filterInterface.getFormattingFilters()
      ),
    };
  };

const getFormattingOptionsOfOpenedWorkspaces = (
  loadedAndConnectedWorkspacesIds: ArdoqId[],
  fields: FieldsState
): PerspectivesFormattingOptions => {
  const workspaceModelIds = new Set(
    loadedAndConnectedWorkspacesIds
      .map(wsId => workspaceInterface.getWorkspaceModelId(wsId))
      .filter(isTruthy)
  );
  const workspaceFields = fields.models.filter(field =>
    workspaceModelIds.has(field.model)
  );
  return {
    appliedConditionalFormattingRules: getAppliedConditionalFormattingRules(),
    referenceFormattingCustomLabels:
      getReferenceFormattingCustomLabels(workspaceFields),
    componentFormattingCustomLabels:
      getComponentFormattingCustomLabels(workspaceFields),
    ...getAppliedComponentFormattingLabelData(),
    ...getAppliedReferenceFormattingLabelData(),
    appliedLabelFormatting: filterInterface.getLabelFormatting(),
    appliedShowReferenceType: getIsShowReferenceTypeApplied(
      filterInterface.getAllReferenceLabelFilters()
    ),
    appliedKeepOriginalColor: !hasDefaultGreyColorFilter(
      filterInterface.getFormattingFilters()
    ),
  };
};

const filterChangedOrEditorOpened$ = merge(
  action$.pipe(
    ofType(
      editorOpened,
      notifyFilterColorChanged,
      notifyFiltersChanged,
      notifyPerspectiveApplied
    )
  ),
  fields$
);

const getViewpointFormattingOptions = (
  optionsData: ViewpointOptionsSourceData
): PerspectivesFormattingOptions => {
  const { availableComponentFields, availableReferenceFields } = optionsData;

  return {
    appliedConditionalFormattingRules: getAppliedConditionalFormattingRules(),
    referenceFormattingCustomLabels: getFormattingCustomLabelsByFields(
      availableReferenceFields
    ),
    componentFormattingCustomLabels: getFormattingCustomLabelsByFields(
      availableComponentFields
    ),
    ...getAppliedComponentFormattingLabelData(),
    ...getAppliedReferenceFormattingLabelData(),
    appliedLabelFormatting: filterInterface.getLabelFormatting(),
    appliedShowReferenceType: getIsShowReferenceTypeApplied(
      filterInterface.getAllReferenceLabelFilters()
    ),
    appliedKeepOriginalColor: !hasDefaultGreyColorFilter(
      filterInterface.getFormattingFilters()
    ),
  };
};

export const getFormattingOptions = (
  workspaceIds: ArdoqId[],
  fields: FieldsState
): PerspectivesFormattingOptions => {
  if (perspectiveInterface.isSavedPerspectiveSelected()) {
    return getFormattingOptionsIncludingSavedPerspectiveEntriesFromClosedWorkspaces(
      workspaceIds,
      fields
    );
  }

  return getFormattingOptionsOfOpenedWorkspaces(workspaceIds, fields);
};

export const perspectiveEditorFormattingOptions$ = combineLatest({
  context: context$,
  viewpointOptionsSourceData: viewpointOptionsSourceData$,
  filterChangedOrEditorOpened$,
  linkedWorkspaces: linkedWorkspaces$,
  fields: fields$,
}).pipe(
  map(
    ({
      context,
      viewpointOptionsSourceData,
      linkedWorkspaces,
      fields,
    }): PerspectivesFormattingOptions => {
      if (viewpointOptionsSourceData) {
        return getViewpointFormattingOptions(viewpointOptionsSourceData);
      }
      const connectedWorkspaceIds = getConnectedWorkspaceIds(
        context.workspaceIds,
        linkedWorkspaces
      );
      return getFormattingOptions(
        uniq([...context.workspaceIds, ...connectedWorkspaceIds]),
        fields
      );
    }
  )
);
